import * as React from 'react';
import styled from 'styled-components';
import { DSColour, getRandomDSColour } from '../../theme/DSColours';
import { PieCirclePartLineProps, calculateCircumference, PieCirclePart } from './PieCirclePart';

const Wrapper = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

interface ContentWrapperDivProps {
    $padding?: number;
}

const ContentWrapperDiv = styled.div<ContentWrapperDivProps>`
    position: absolute;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    padding: 0 ${({ $padding }) => $padding}px;
    max-width: 100%;

    flex-direction: column;
`;

const DEFAULT_SIZE = 200;
const DEFAULT_STROKE_WIDTH = 15;

export function getNestedPieCircleSize(
    childCount: number,
    baseSize = DEFAULT_SIZE,
    strokeWidth = DEFAULT_STROKE_WIDTH,
): number {
    return baseSize + strokeWidth * 2 * childCount;
}

type PieCircleParts = PieCirclePart[];

type PieCircleOrNumberParts = Array<number | (Partial<PieCirclePart> & Pick<PieCirclePart, 'value'>)>;

type PieCirclePartLinePropsWithDefaults = Omit<PieCirclePartLineProps, 'colour'> &
    Partial<Pick<PieCirclePartLineProps, 'colour'>>;

function pieCircleOrNumberPartsToParts(
    parts: PieCircleOrNumberParts,
    defaults: PieCirclePartLinePropsWithDefaults,
): PieCircleParts {
    return parts.map((part, index) => {
        if (typeof part === 'number') {
            return {
                colour: getRandomDSColour(index),
                ...defaults,
                value: part,
            };
        }
        return {
            colour: getRandomDSColour(index),
            ...defaults,
            ...part,
        };
    });
}

// eslint-disable-next-line max-statements
function partsWithOffsets(parts: PieCirclePart[]): Array<PieCirclePart & { offset: number }> {
    const newParts: Array<PieCirclePart & { offset: number }> = [];
    let previousEnd = 0;
    let lastShrinkSizePercent = 0;
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const currentShrinkSizePercent =
            part.offsetBetween / calculateCircumference(part.radius, part.strokeWidth).circumference;

        let offset: number;
        let shrink: number;

        if (i === 0) {
            offset = currentShrinkSizePercent;
            shrink = currentShrinkSizePercent * 2;
        } else if (i === parts.length) {
            offset = previousEnd + lastShrinkSizePercent * 2;
            shrink = lastShrinkSizePercent;
        } else {
            offset = previousEnd + lastShrinkSizePercent * 2;
            shrink = currentShrinkSizePercent * 2;
        }
        const adjustedValue = part.value - shrink;
        const forcedVisibleValue = part.value < 0.01 ? adjustedValue : Math.max(adjustedValue, 0.01);
        newParts.push({ ...part, offset, value: forcedVisibleValue });
        previousEnd = offset + forcedVisibleValue;
        lastShrinkSizePercent = currentShrinkSizePercent;
    }
    return newParts;
}

export type PieCircleProps = Partial<PieCirclePartLineProps> & {
    /**
     * The progress in percentage format 0 - 1 (0.5 = 50%)
     */
    parts: PieCircleOrNumberParts;
    children?: React.ReactNode;
};

export function PieCircle({
    children,
    parts,
    radius = DEFAULT_SIZE,
    ...defaultLineProps
}: PieCircleProps): JSX.Element {
    const linePropsWithDefaults: PieCirclePartLinePropsWithDefaults = {
        animateIn: true,
        animationInDelay: 0,
        animationTime: 500,
        background: false,
        offsetBetween: (defaultLineProps.strokeWidth ?? DEFAULT_STROKE_WIDTH) * 0.7,
        radius,
        strokeCap: 'round',
        strokeWidth: DEFAULT_STROKE_WIDTH,
        ...defaultLineProps,
    };
    const fixedParts = pieCircleOrNumberPartsToParts(parts, linePropsWithDefaults);
    const fixedPartsWithOffsets = partsWithOffsets(fixedParts);
    return (
        <Wrapper>
            <svg height={radius} width={radius}></svg>
            {fixedPartsWithOffsets.map((part, index) => (
                <ContentWrapperDiv $padding={radius / 4} key={index}>
                    <PieCirclePart {...part} offset={part.offset} />
                </ContentWrapperDiv>
            ))}
            {children && <ContentWrapperDiv $padding={radius / 4}>{children}</ContentWrapperDiv>}
        </Wrapper>
    );
}
PieCircle.displayName = 'PieCircle';
PieCircle.colours = DSColour;
PieCircle.getNestedSize = getNestedPieCircleSize;
